/**
 * Phase 9A · Shared Quick Items catalog picker + create modal.
 *
 * ONE React surface used by Historical Entry, Quick Sale, and
 * Sales Orders. All three surfaces read + write the SAME backend
 * collection (`db.historical_quick_items`) via
 * `/api/admin/historical-quick-items`, so an item created from any
 * workflow immediately becomes available in the other two — no
 * duplicate records, single source of truth.
 *
 * The backend collection name is NOT renamed here; renaming would
 * force a migration for zero user-visible benefit. Only the FE
 * label changed to `Quick Items`.
 *
 * Cart-line shape emitted by `quickItemToCustomLine()` matches the
 * canonical `custom_lines[]` schema already consumed by
 * `order_services/mixed_sale_pricing.py::augment_snapshot`. Both
 * `POST /api/admin/sticker-orders/manual` (Historical Entry, Quick
 * Sale) and `POST /api/admin/sales-orders` accept the exact same
 * shape verbatim.
 */
import React, { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Plus, Search, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;


/** Load the shared Quick Items catalog. Returns
 *  ``{ items, loading, reload }``. `items` is sorted by usage_count
 *  DESC → name ASC (the same order the backend returns). */
export function useQuickItemsCatalog(active = true) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/historical-quick-items",
                              { params: { active, limit: 200 } });
      setItems(r.data?.items || []);
    } catch { setItems([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { reload(); },
    /* eslint-disable-next-line react-hooks/exhaustive-deps */ []);
  return { items, loading, reload };
}


/** Convert a picked Quick Item + operator-entered qty/price into a
 *  `custom_lines[]` entry ready to send to any Phase 2 write path
 *  (New Sale, Quick Sale, Sales Order). */
export function quickItemToCustomLine(item, opts = {}) {
  const qty = Math.max(1, Math.floor(Number(opts.qty || 1) || 1));
  const unit_price = Math.round(Number(opts.unit_price || 0) * 100) / 100;
  const line = {
    id: (typeof crypto !== "undefined" && crypto.randomUUID)
        ? crypto.randomUUID() : String(Date.now()),
    description: item.name,
    qty,
    unit_price,
    taxable: opts.taxable !== false,
    kind: "historical_quick_item",
    historical_quick_item_id: item.id,
    historical_quick_item_name: item.name,
    historical_quick_item_category: item.category || undefined,
  };
  if (opts.note) line.historical_note = String(opts.note).trim() || undefined;
  return line;
}


/** Fire-and-forget `touch` bump. Do NOT block the UI on this. */
export function touchQuickItem(itemId) {
  if (!itemId) return;
  api.post(`/admin/historical-quick-items/${itemId}/touch`)
     .catch(() => { /* silent — usage_count is best-effort */ });
}


/**
 * Sheet-style Quick Items picker.
 *
 * Props:
 *   • onPick(item, {qty, unit_price, taxable})  — add line to cart
 *   • onClose()                                  — dismiss sheet
 *   • testidPrefix                               — data-testid namespace
 */
export function QuickItemsPickerSheet({ onPick, onClose, testidPrefix = "quick-items" }) {
  const { items, loading, reload } = useQuickItemsCatalog(true);
  const [q, setQ] = useState("");
  const [pick, setPick] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return items;
    return items.filter(
      (it) => (it.name || "").toLowerCase().includes(needle)
             || (it.category || "").toLowerCase().includes(needle));
  }, [items, q]);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center"
         data-testid={`${testidPrefix}-sheet`}>
      <div className="bg-white rounded-t-2xl sm:rounded-2xl border-2 border-[var(--ink)]
                     w-full max-w-md p-3 pb-24 sm:pb-3 space-y-3 max-h-[92vh] overflow-auto">
        <div className="flex items-center justify-between">
          <div className="font-bold text-sm">Quick Items</div>
          <button onClick={onClose} data-testid={`${testidPrefix}-close`}><X size={20}/></button>
        </div>

        {pick ? (
          <QuickItemLineEditor item={pick}
                                    onCancel={() => setPick(null)}
                                    onConfirm={(cfg) => {
                                      onPick(pick, cfg);
                                      touchQuickItem(pick.id);
                                    }}
                                    testidPrefix={testidPrefix}/>
        ) : (
          <>
            <div className="relative">
              <Search size={14} className="absolute left-2 top-3 text-[var(--smoke)]"/>
              <input value={q} onChange={(e) => setQ(e.target.value)}
                     placeholder="Search Quick Items…"
                     className="w-full border-2 border-[var(--ink)] rounded-xl pl-7 pr-2 py-2 text-sm"
                     data-testid={`${testidPrefix}-search`}/>
            </div>

            <button onClick={() => setShowAdd(true)}
                    className="w-full border-2 border-dashed border-[var(--ink)] rounded-xl
                               py-2 text-sm font-semibold flex items-center justify-center gap-1"
                    data-testid={`${testidPrefix}-add-new`}>
              <Plus size={14}/> New Quick Item
            </button>

            {loading ? (
              <div className="text-xs text-[var(--smoke)] p-3">Loading…</div>
            ) : filtered.length === 0 ? (
              <div className="text-xs text-[var(--smoke)] p-3 border-2 border-dashed rounded-xl"
                   data-testid={`${testidPrefix}-empty`}>
                {q ? "No Quick Items match." :
                  "No Quick Items yet. Tap New to add reusable non-Shopify entries " +
                  "(Cajita, Bucket Hat, Jersey, …)."}
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2"
                   data-testid={`${testidPrefix}-grid`}>
                {filtered.map((it) => (
                  <button key={it.id} onClick={() => setPick(it)}
                          className="border-2 border-[var(--ink)] rounded-xl p-2
                                    bg-white text-left active:bg-red-50 min-h-[64px]"
                          data-testid={`${testidPrefix}-item-${it.id}`}>
                    <div className="text-sm font-semibold line-clamp-2">{it.name}</div>
                    {it.default_total != null && (
                      <div className="text-[11px] text-[var(--panini-red)] mt-1">
                        Default {money(it.default_total)}
                      </div>
                    )}
                    {!!it.category && (
                      <div className="text-[10px] text-[var(--smoke)] uppercase mt-1">
                        {it.category}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {showAdd && (
        <AddQuickItemModal onClose={() => setShowAdd(false)}
                              onCreated={async (item) => {
                                setShowAdd(false);
                                await reload();
                                if (item) setPick(item);
                              }}
                              testidPrefix={testidPrefix}/>
      )}
    </div>
  );
}


function QuickItemLineEditor({ item, onCancel, onConfirm, testidPrefix }) {
  const [qty, setQty] = useState("1");
  const [price, setPrice] = useState(
    item.default_total != null ? Number(item.default_total).toFixed(2) : "");
  const [taxable, setTaxable] = useState(true);
  const [note, setNote] = useState("");
  const qtyInt = Math.max(1, Math.floor(Number(qty) || 0) || 1);
  const unitPrice = Number(price) || 0;
  const canConfirm = unitPrice > 0;

  return (
    <div className="space-y-3" data-testid={`${testidPrefix}-line-editor`}>
      <div className="flex items-start gap-2">
        <button onClick={onCancel} data-testid={`${testidPrefix}-back`}><X size={18}/></button>
        <div className="flex-1">
          <div className="text-[10px] uppercase text-[var(--smoke)]">Quick Item</div>
          <div className="font-bold text-base">{item.name}</div>
          {!!item.category && (
            <div className="text-[10px] text-[var(--smoke)] uppercase">{item.category}</div>
          )}
        </div>
      </div>
      <label className="block">
        <span className="text-sm font-semibold">Quantity</span>
        <input type="number" min="1" step="1" value={qty} inputMode="numeric"
               onChange={(e) => setQty(e.target.value)}
               onBlur={() => { const n = Math.floor(Number(qty));
                                setQty(String(n >= 1 ? n : 1)); }}
               className="mt-1 w-full border-2 border-[var(--ink)] rounded-2xl px-3 py-3 text-lg font-bold"
               data-testid={`${testidPrefix}-qty`}/>
      </label>
      <label className="block">
        <span className="text-sm font-semibold">Unit price (pre-tax)</span>
        <input type="number" min="0" step="0.01" value={price} inputMode="decimal"
               onChange={(e) => setPrice(e.target.value)}
               className="mt-1 w-full border-2 border-[var(--ink)] rounded-2xl px-3 py-3 text-lg font-bold"
               data-testid={`${testidPrefix}-price`}/>
      </label>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={taxable}
               onChange={(e) => setTaxable(e.target.checked)}
               data-testid={`${testidPrefix}-taxable`}/>
        <span>Taxable</span>
      </label>
      <label className="block">
        <span className="text-sm font-semibold">Note (optional)</span>
        <input type="text" value={note} onChange={(e) => setNote(e.target.value)}
               className="mt-1 w-full border-2 border-[var(--ink)] rounded-2xl px-3 py-2 text-sm"
               data-testid={`${testidPrefix}-note`}/>
      </label>
      <button disabled={!canConfirm}
              onClick={() => onConfirm({ qty: qtyInt, unit_price: unitPrice, taxable,
                                              note: note.trim() || undefined })}
              className={`w-full rounded-2xl py-3 font-bold text-white ${
                canConfirm ? "bg-[var(--panini-red,_#dc2626)]" : "bg-gray-400"
              }`}
              data-testid={`${testidPrefix}-confirm`}>
        Add to Cart · {money(qtyInt * unitPrice)}
      </button>
    </div>
  );
}


/** Standalone modal for creating a new Quick Item. Idempotent by
 *  normalized_name on the backend — safe to call repeatedly. */
export function AddQuickItemModal({ onClose, onCreated,
                                     testidPrefix = "quick-items" }) {
  const [name, setName] = useState("");
  const [category, setCategory] = useState("");
  const [defTotal, setDefTotal] = useState("");
  const [saving, setSaving] = useState(false);
  const [dupe, setDupe] = useState(null);

  useEffect(() => {
    const n = name.trim();
    if (!n) { setDupe(null); return; }
    let alive = true;
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/admin/historical-quick-items/preflight",
                                  { params: { name: n } });
        if (alive) setDupe(r.data?.shopify_match || null);
      } catch { if (alive) setDupe(null); }
    }, 300);
    return () => { alive = false; clearTimeout(t); };
  }, [name]);

  async function create() {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const r = await api.post("/admin/historical-quick-items", {
        name: name.trim(),
        category: category.trim() || null,
        default_total: defTotal ? Number(defTotal) : null,
      });
      toast.success(r.data?.created ? "Quick Item created."
                                     : "Quick Item exists — reused.");
      onCreated(r.data?.item || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed.");
    } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center"
         data-testid={`${testidPrefix}-add-modal`}>
      <div className="bg-white rounded-t-2xl sm:rounded-2xl border-2 border-[var(--ink)]
                     w-full max-w-md p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="font-bold">+ New Quick Item</div>
          <button onClick={onClose}><X size={20}/></button>
        </div>
        <div className="text-[11px] text-[var(--smoke)]">
          Shared catalog — appears in Historical Entry, Quick Sale, and
          Sales Orders. For discontinued or non-Shopify merchandise only.
        </div>
        <label className="block">
          <span className="text-sm font-semibold">Name</span>
          <input value={name} onChange={(e) => setName(e.target.value)}
                 placeholder="e.g. Cajita"
                 className="mt-1 w-full border-2 border-[var(--ink)] rounded-2xl px-3 py-3 text-base"
                 data-testid={`${testidPrefix}-add-name`}/>
        </label>
        {dupe && (
          <div className="border-2 border-yellow-400 bg-yellow-50 rounded-xl p-2 text-xs flex items-start gap-2"
               data-testid={`${testidPrefix}-add-shopify-dupe`}>
            <AlertTriangle size={14} className="text-yellow-700 mt-0.5 flex-shrink-0"/>
            <div>
              <div className="font-semibold text-yellow-900">
                "{dupe.title}" already exists in Shopify.
              </div>
              <div className="text-yellow-800 mt-0.5">
                Use the Shopify product so lifetime sales stay grouped.
              </div>
            </div>
          </div>
        )}
        <label className="block">
          <span className="text-sm font-semibold">Category (optional)</span>
          <input value={category} onChange={(e) => setCategory(e.target.value)}
                 placeholder="e.g. Merch"
                 className="mt-1 w-full border rounded-2xl px-3 py-2 text-sm"
                 data-testid={`${testidPrefix}-add-category`}/>
        </label>
        <label className="block">
          <span className="text-sm font-semibold">Default total (optional)</span>
          <input type="number" min="0" step="0.01" value={defTotal}
                 onChange={(e) => setDefTotal(e.target.value)}
                 placeholder="0.00"
                 className="mt-1 w-full border rounded-2xl px-3 py-2 text-sm"
                 data-testid={`${testidPrefix}-add-default`}/>
        </label>
        <button onClick={create} disabled={!name.trim() || saving}
                className={`w-full rounded-2xl py-3 font-bold text-white ${
                  !name.trim() || saving ? "bg-gray-400" : "bg-[var(--panini-red,_#dc2626)]"
                }`}
                data-testid={`${testidPrefix}-add-confirm`}>
          {saving ? "Saving…" : "Save Quick Item"}
        </button>
      </div>
    </div>
  );
}
